import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Home, BookOpen, ClipboardList, Trophy, Gamepad2, ChevronRight, Users, Layers,
  Crown, Sparkles, Grid3x3, Star, LogOut, Award, User, Sword, FileText, Database, Video, ScrollText, Shield, Flame, KeyRound, School, GraduationCap, Target, ClipboardCheck, Menu, X
} from 'lucide-react';

const menus = {
  student: [
    { to:'/student/dashboard', label:'Inicio', icon: Home },
    { to:'/student/classrooms', label:'Mis clases', icon: School, testid:'nav-salones' },
    { type:'section', key:'aprendizaje', label:'Mi aprendizaje', icon: BookOpen, children: [
      { to:'/student/courses', label:'Cursos', icon: BookOpen, testid:'nav-mi-aprendizaje' },
      { to:'/student/chess', label:'Tablero', icon: Grid3x3 },
      { to:'/student/coach-history', label:'Entrenador IA', icon: Award, testid:'nav-mi-entrenador-ia' },
    ]},
    { type:'section', key:'actividades', label:'Actividades', icon: Target, children: [
      { to:'/student/quizzes', label:'Quizzes', icon: Target },
      { to:'/student/assignments', label:'Tareas', icon: ClipboardList },
      { to:'/student/challenges', label:'Desafíos', icon: Flame },
    ]},
    { to:'/student/profile', label:'Perfil', icon: User, testid:'nav-mi-perfil' },
  ],
  teacher: [
    { to:'/teacher/dashboard', label:'Inicio', icon: Home, testid:'nav-dashboard' },
    { to:'/teacher/classrooms', label:'Salones', icon: School },
    { to:'/teacher/studies', label:'Estudios', icon: BookOpen },
    { to:'/teacher/groups', label:'Grupos', icon: Users, testid:'nav-grupos' },
    { type:'section', key:'actividades', label:'Actividades', icon: Target, children: [
      { to:'/teacher/quizzes', label:'Quizzes', icon: Target },
    ]},
    { type:'section', key:'mas', label:'Más', icon: Layers, children: [
      { to:'/teacher/recaps', label:'Recaps', icon: ScrollText },
      { to:'/teacher/database', label:'Base de datos', icon: Database },
      { to:'/teacher/profile', label:'Mi perfil', icon: User },
    ]},
  ],
  super_admin: [
    { to:'/admin/dashboard', label:'Dashboard', icon: Home },
    // Fase B-5A-UI: agrupar Usuarios y Grupos bajo Academia (solo navegación)
    { type:'section', key:'academia', label:'Academia', icon: GraduationCap, children: [
      { to:'/admin/users', label:'Usuarios', icon: Users },
      { to:'/admin/groups', label:'Grupos', icon: Layers },
    ]},
    { to:'/admin/classrooms', label:'Salones', icon: School },
    { to:'/admin/courses', label:'Cursos', icon: BookOpen },
    { to:'/admin/database', label:'Base de datos', icon: Database },
    { to:'/admin/studies', label:'Estudios', icon: GraduationCap },
    { to:'/admin/quizzes', label:'Quizzes', icon: Target },
    { to:'/admin/module-exam-requests', label:'Solicitudes de examen', icon: GraduationCap },
    { to:'/admin/module-exam-reviews', label:'Revisión de exámenes', icon: ClipboardCheck },
    { to:'/admin/audit', label:'Auditoría', icon: ScrollText },
    { to:'/admin/profile', label:'Mi perfil', icon: User },
    { to:'/admin/login-history', label:'Accesos', icon: KeyRound },
  ],
  // Fase B-4C-2: menú restringido para assistant_admin
  assistant_admin: [
    { to:'/admin/dashboard', label:'Dashboard', icon: Home },
    { type:'section', key:'operacion', label:'Operación', icon: GraduationCap, children: [
      { to:'/admin/users', label:'Alumnos', icon: Users },
      { to:'/admin/groups', label:'Grupos', icon: Layers },
      { to:'/admin/tasks', label:'Seguimiento', icon: ClipboardCheck },
    ]},
    { to:'/admin/profile', label:'Mi perfil', icon: User },
  ],
};

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  // Fase B-4C-2: assistant_admin usa su propio menú (no colapsar a super_admin)
  const roleKey = (user?.role === 'school_admin' || user?.role === 'partner_admin') ? 'super_admin' : user?.role;
  const items = (menus[roleKey] || []).filter(
    item =>
      user?.role !== 'partner_admin' ||
      (
        item.to !== '/admin/module-exam-requests' &&
        item.to !== '/admin/module-exam-reviews'
      )
  ).filter(
    item =>
      user?.role !== 'assistant_admin' ||
      item.to !== '/admin/module-exam-reviews'
  );

  const renderLink = ({to, label, icon: Icon, testid}, extraClass = '') => (
    <NavLink
      key={to}
      to={to}
      onClick={onNavigate}
      data-testid={testid || `nav-${label.toLowerCase().replace(/\s/g,'-')}`}
      className={({isActive}) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-semibold transition-all duration-200 ${extraClass} ${
          isActive
            ? 'bg-[#6789B2] text-white shadow-sm'
            : 'text-white hover:bg-white/10 hover:text-white'
        }`
      }
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <>
      <Link to="/app" onClick={onNavigate} className="flex items-center gap-3 px-5 py-6 group">
        <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm grid place-items-center shadow-sm transition-transform duration-200 group-hover:scale-105">
          <Crown className="w-5 h-5 text-white"/>
        </div>
        <div>
          <div className="font-display font-extrabold leading-tight text-white tracking-tight">Chess4Kings</div>
          <div className="text-xs text-white/60 leading-tight tracking-wide">ACADEMY</div>
        </div>
      </Link>
      <nav className="px-3 py-2 space-y-0.5 flex-1 overflow-y-auto">
        {items.map((it) => {
          if (it.type === 'section') {
            const Icon = it.icon;
            const sectionActive = it.children?.some(c => loc.pathname.startsWith(c.to));
            return (
              <div key={it.key} data-testid={`nav-section-${it.key}`} className="pt-2">
                <div className={`flex items-center gap-3 px-3 py-2 rounded-lg text-xs uppercase tracking-widest font-bold ${
                  sectionActive ? 'text-white bg-white/10' : 'text-white/95'
                }`}>
                  <Icon className="w-4 h-4" />
                  <span>{it.label}</span>
                </div>
                <div className="ml-2 pl-3 border-l border-white/20 space-y-0.5 py-1">
                  {it.children.map(c => renderLink(c, 'text-[15px]'))}
                </div>
              </div>
            );
          }
          return renderLink(it);
        })}
      </nav>
      <div className="p-3 border-t border-white/15 bg-black/10">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-[#6789B2] border border-white/30 grid place-items-center text-white font-bold shadow-sm">
            {user?.first_name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <div className="font-semibold text-sm truncate text-white">{user?.first_name} {user?.last_name}</div>
              {user?.is_owner && (
                <span data-testid="owner-badge" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 text-white text-[9px] font-extrabold uppercase tracking-widest shrink-0">
                  <Shield className="w-2.5 h-2.5"/> Owner
                </span>
              )}
            </div>
            <div className="text-xs text-white/60 capitalize">{user?.role?.replace('_',' ')}</div>
          </div>
          <Button data-testid="logout-btn" variant="ghost" size="icon" onClick={logout} className="text-white/70 hover:text-white hover:bg-white/10"><LogOut className="w-4 h-4"/></Button>
        </div>
      </div>
    </>
  );
}

export default function AppLayout() {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();
  // Cierra el drawer al navegar a otra ruta.
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar desktop (idéntico al comportamiento previo) */}
      <aside className="hidden md:flex w-64 bg-[#002537] bg-cover bg-center bg-no-repeat text-white h-screen sticky top-0 flex-col shrink-0 shadow-xl border-r-4 border-[#f59e0b]" style={{ backgroundImage: "url(/branding/castillo-real-tablero.webp)" }}>
        <SidebarContent />
      </aside>

      {/* Drawer móvil */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-sidebar-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} data-testid="mobile-sidebar-backdrop" />
          <aside className="absolute left-0 top-0 w-64 max-w-[80vw] bg-[#002537] text-white h-screen flex flex-col shrink-0 shadow-2xl border-r-4 border-[#0B5D8F]">
            <button
              data-testid="mobile-menu-close"
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 p-2 rounded-lg hover:bg-slate-100 z-10"
              aria-label="Cerrar menú"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Barra superior móvil con botón hamburguesa */}
        <header className="md:hidden sticky top-0 z-40 flex items-center gap-3 bg-white border-b border-slate-200 px-4 h-14">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 rounded-lg hover:bg-slate-100"
            aria-label="Abrir menú"
          >
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/app" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-slate-900 grid place-items-center"><Crown className="w-4 h-4 text-amber-300"/></div>
            <div className="font-display font-extrabold leading-tight">Chess4Kings</div>
          </Link>
        </header>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
